import React, { useState, useEffect, useMemo } from 'react';
import Header from '../../components/Header';
import SearchableSelect from '../../components/SearchableSelect';
import { colors } from '../../styles/theme';
import RebuildHistoryModal from './RebuildHistoryModal';
import { logError } from '../../utils/logError';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const getAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}`, ...extra } : extra;
};

const ALIS_BIRIMLERI = ['KOLI', 'KASA', 'PAKET', 'TNK', 'KUTU', 'TORBA', 'ADET', 'LITRE', 'KG', 'GR', 'ML', 'PORSIYON', 'METRE', 'RULO'];
const STOK_BIRIMLERI = ['KG', 'GR', 'LITRE', 'ML', 'ADET', 'PORSIYON', 'PAKET', 'METRE'];

export default function BirimDonusumPage() {
  const [malzemeler, setMalzemeler] = useState([]);
  const [donusumler, setDonusumler] = useState([]);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState(null);

  // Eksik dönüşümlü malzemeler
  const [eksikData, setEksikData] = useState({ toplam_aktif: 0, eksik_sayisi: 0, malzemeler: [] });
  const [showEksikList, setShowEksikList] = useState(false);

  // Form
  const [malzemeId, setMalzemeId] = useState('');
  const [alisBirimi, setAlisBirimi] = useState('KOLI');
  const [stokBirimi, setStokBirimi] = useState('KG');
  const [katsayi, setKatsayi] = useState('');
  const [aciklama, setAciklama] = useState('');
  const [editId, setEditId] = useState(null);

  // Rebuild history modal
  const [rebuildId, setRebuildId] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [mRes, dRes, eRes] = await Promise.all([
        fetch(`${API_URL}/api/malzemeler`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/birim-donusumleri`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/malzemeler/eksik-birim-donusumlu`, { headers: getAuthHeaders() }),
      ]);
      if (mRes.ok) setMalzemeler(await mRes.json());
      if (dRes.ok) setDonusumler(await dRes.json());
      if (eRes.ok) setEksikData(await eRes.json());
    } catch (err) { logError(err); }
    setLoading(false);
  };

  const handleQuickEdit = (m) => {
    // Eksik listeden bir malzeme tıklandığında form'a doldur
    setEditId(null);
    setMalzemeId(m.id);
    setAlisBirimi('KOLI');
    setKatsayi('');
    setAciklama('');
    // Sayfayı form'a scroll et
    const formEl = document.getElementById('birim-donusum-form');
    if (formEl) formEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const malzemeOptions = useMemo(() =>
    (malzemeler || []).map(m => ({ value: m.id, label: `${m.kod || ''} - ${m.ad} (${m.birim})` })),
    [malzemeler]
  );

  const selectedMalzeme = malzemeler.find(m => m.id === (typeof malzemeId === 'string' ? parseInt(malzemeId) : malzemeId));

  const handleSubmit = async () => {
    if (!malzemeId || !katsayi || parseFloat(katsayi) <= 0) {
      setMsg({ type: 'error', text: 'Malzeme ve geçerli bir katsayı girin' });
      return;
    }
    setMsg(null);
    try {
      const body = {
        malzeme_id: typeof malzemeId === 'string' ? parseInt(malzemeId) : malzemeId,
        alis_birimi: alisBirimi,
        stok_birimi: stokBirimi,
        katsayi: parseFloat(katsayi),
        aciklama: aciklama || null,
      };

      let res;
      if (editId) {
        res = await fetch(`${API_URL}/api/birim-donusumleri/${editId}`, {
          method: 'PUT',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(body)
        });
      } else {
        res = await fetch(`${API_URL}/api/birim-donusumleri`, {
          method: 'POST',
          headers: getAuthHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(body)
        });
      }

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Hata');
      const wasCreate = !editId;
      setMsg({ type: 'success', text: editId ? 'Dönüşüm güncellendi' : 'Dönüşüm eklendi' });
      resetForm();
      loadData();
      // Task 11: Yeni oluşturulduysa ve geçmişte dönüştürülecek kayıt varsa modal'ı otomatik aç
      if (wasCreate && data?.id && data?.dry_run_ozet?.donusturulecek > 0) {
        setRebuildId(data.id);
      }
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const handleEdit = (d) => {
    setEditId(d.id);
    setMalzemeId(d.malzeme_id);
    setAlisBirimi(d.alis_birimi);
    setKatsayi(String(d.katsayi));
    setAciklama(d.aciklama || '');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu dönüşümü silmek istediğinize emin misiniz?')) return;
    try {
      const res = await fetch(`${API_URL}/api/birim-donusumleri/${id}`, {
        method: 'DELETE', headers: getAuthHeaders()
      });
      if (!res.ok) throw new Error('Silinemedi');
      setMsg({ type: 'success', text: 'Silindi' });
      loadData();
    } catch (err) {
      setMsg({ type: 'error', text: err.message });
    }
  };

  const resetForm = () => {
    setEditId(null);
    setMalzemeId('');
    setAlisBirimi('KOLI');
    setStokBirimi('KG');
    setKatsayi('');
    setAciklama('');
  };

  // Malzeme değiştiğinde stok birimini güncelle (varsayılan)
  React.useEffect(() => {
    if (selectedMalzeme?.birim) setStokBirimi(selectedMalzeme.birim.toUpperCase());
  }, [selectedMalzeme]);

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7fa' }}>
      <Header />
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '24px 16px' }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: '0 0 20px' }}>🔄 Birim Dönüşümleri</h1>
        <p style={{ color: colors.textSecondary, fontSize: 14, margin: '0 0 20px' }}>
          Tedarikçiden farklı birimle gelen ürünlerin stok birimine dönüşüm katsayılarını tanımlayın.
          Fiyat girişi ve mal kabul onayında otomatik uygulanır.
        </p>

        {msg && (
          <div style={{
            padding: '10px 16px', borderRadius: 8, marginBottom: 16,
            background: msg.type === 'success' ? '#e8f5e9' : '#ffebee',
            color: msg.type === 'success' ? '#2e7d32' : '#c62828',
            border: `1px solid ${msg.type === 'success' ? '#a5d6a7' : '#ef9a9a'}`
          }}>{msg.text}</div>
        )}

        {/* Eksik Dönüşüm Uyarı Kartı */}
        {eksikData.eksik_sayisi > 0 && (
          <div style={{
            background: '#fff8e1', border: '1px solid #ffe082',
            borderRadius: 12, padding: '16px 20px', marginBottom: 16,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            flexWrap: 'wrap', gap: 12
          }}>
            <div style={{ flex: 1, minWidth: 260 }}>
              <div style={{ fontWeight: 700, color: '#e65100', marginBottom: 4, fontSize: 15 }}>
                ⚠️ Dönüşüm Eksikliği Uyarısı — {eksikData.eksik_sayisi} malzeme
              </div>
              <div style={{ fontSize: 13, color: '#856404', lineHeight: 1.5 }}>
                Bu malzemeler için birim dönüşümü tanımlı değil. Fiyat girişi sırasında
                farklı bir alış birimi kullanıldığında sistem hata verecektir.
                <br />
                <span style={{ color: colors.textTertiary, fontSize: 12 }}>
                  Toplam aktif malzeme: {eksikData.toplam_aktif} · Dönüşümü olan: {eksikData.toplam_aktif - eksikData.eksik_sayisi}
                </span>
              </div>
            </div>
            <button
              onClick={() => setShowEksikList(!showEksikList)}
              style={{
                padding: '8px 16px', background: '#f57c00', color: '#fff',
                border: 'none', borderRadius: 8, cursor: 'pointer',
                fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap'
              }}
            >
              {showEksikList ? 'Listeyi Gizle' : 'Eksikleri Listele →'}
            </button>
          </div>
        )}

        {eksikData.eksik_sayisi === 0 && (
          <div style={{
            background: '#e8f5e9', border: '1px solid #a5d6a7',
            borderRadius: 12, padding: '12px 20px', marginBottom: 16,
            color: '#2e7d32', fontSize: 14, fontWeight: 600
          }}>
            ✅ Tüm aktif malzemeler için birim dönüşümü tanımlı.
          </div>
        )}

        {/* Eksik Liste */}
        {showEksikList && eksikData.malzemeler.filter(m => m.eksik).length > 0 && (
          <div style={{
            background: colors.surface, borderRadius: 12, padding: 16, marginBottom: 16,
            boxShadow: '0 1px 4px rgba(0,0,0,0.08)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h4 style={{ margin: 0, fontSize: 14 }}>Dönüşümü Eksik Malzemeler ({eksikData.eksik_sayisi})</h4>
              <span style={{ fontSize: 12, color: colors.textSecondary }}>Bir malzemeye tıklayın → form'a gelir</span>
            </div>
            <div style={{ overflowX: 'auto', maxHeight: 300 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: colors.backgroundAlt, position: 'sticky', top: 0 }}>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#555', borderBottom: `1px solid ${colors.border}` }}>Kod</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#555', borderBottom: `1px solid ${colors.border}` }}>Ad</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#555', borderBottom: `1px solid ${colors.border}` }}>Kategori</th>
                    <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#555', borderBottom: `1px solid ${colors.border}` }}>Stok Birimi</th>
                    <th style={{ padding: '8px 10px', textAlign: 'center', fontWeight: 700, color: '#555', borderBottom: `1px solid ${colors.border}` }}>İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {eksikData.malzemeler.filter(m => m.eksik).map(m => (
                    <tr key={m.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: colors.textSecondary, fontSize: 12 }}>{m.kod}</td>
                      <td style={{ padding: '8px 10px', fontWeight: 600 }}>{m.ad}</td>
                      <td style={{ padding: '8px 10px', color: colors.textSecondary }}>{m.kategori || '-'}</td>
                      <td style={{ padding: '8px 10px' }}>{m.stok_birimi}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <button onClick={() => handleQuickEdit(m)} style={{
                          padding: '4px 12px', background: '#1a73e8', color: '#fff',
                          border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600
                        }}>+ Ekle</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Form */}
        <div id="birim-donusum-form" style={{ background: colors.surface, borderRadius: 12, padding: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: 24 }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, fontWeight: 600 }}>
            {editId ? '✏️ Dönüşüm Düzenle' : '➕ Yeni Dönüşüm'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 16, marginBottom: 16, alignItems: 'end' }}>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Malzeme</label>
              <SearchableSelect
                options={malzemeOptions}
                value={malzemeId}
                onChange={v => setMalzemeId(v ? parseInt(v) : '')}
                placeholder="Malzeme arayın..."
                maxVisible={50}
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Alış Birimi</label>
              <select value={alisBirimi} onChange={e => setAlisBirimi(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}>
                {ALIS_BIRIMLERI.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Stok Birimi</label>
              <select value={stokBirimi} onChange={e => setStokBirimi(e.target.value)}
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, background: '#fff', boxSizing: 'border-box' }}>
                {STOK_BIRIMLERI.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Katsayı</label>
              <input type="number" step="0.01" min="0.01" value={katsayi} onChange={e => setKatsayi(e.target.value)}
                placeholder="ör. 10"
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
          </div>

          {/* Hesap gösterimi */}
          {selectedMalzeme && katsayi && parseFloat(katsayi) > 0 && (
            <div style={{
              padding: '12px 16px', background: '#e3f2fd', borderRadius: 8, marginBottom: 16,
              fontSize: 14, color: '#1565c0', fontWeight: 600
            }}>
              1 {alisBirimi} × {katsayi} = {parseFloat(katsayi)} {stokBirimi}
              <span style={{ fontWeight: 400, marginLeft: 12, color: colors.textSecondary }}>
                ({selectedMalzeme.ad})
              </span>
              <br />
              <span style={{ fontWeight: 400, fontSize: 13, color: '#555' }}>
                Örn: 500₺/{alisBirimi} → {(500 / parseFloat(katsayi)).toFixed(2)}₺/{stokBirimi}
              </span>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, alignItems: 'end' }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Açıklama (opsiyonel)</label>
              <input value={aciklama} onChange={e => setAciklama(e.target.value)} placeholder="ör. Farm Frites 2.5kg koli"
                style={{ width: '100%', padding: '8px 12px', border: `1px solid ${colors.border}`, borderRadius: 6, fontSize: 14, boxSizing: 'border-box' }}
              />
            </div>
            <button onClick={handleSubmit} style={{
              padding: '10px 24px', background: '#1a73e8', color: '#fff', border: 'none', borderRadius: 8,
              fontSize: 14, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap'
            }}>
              {editId ? 'Güncelle' : 'Ekle'}
            </button>
            {editId && (
              <button onClick={resetForm} style={{
                padding: '10px 16px', background: '#e0e0e0', color: '#333', border: 'none', borderRadius: 8,
                fontSize: 14, cursor: 'pointer', whiteSpace: 'nowrap'
              }}>İptal</button>
            )}
          </div>
        </div>

        {/* Tablo */}
        <div style={{ background: colors.surface, borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
          <div style={{ padding: '16px 20px', borderBottom: `1px solid ${colors.border}`, fontWeight: 700, fontSize: 16 }}>
            Tanımlı Dönüşümler ({donusumler.length})
          </div>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: colors.textTertiary }}>Yükleniyor...</div>
          ) : donusumler.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: colors.textTertiary }}>Henüz dönüşüm tanımlanmamış</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead>
                <tr style={{ background: colors.backgroundAlt, borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Malzeme</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>Alış Birimi</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>→</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>Stok Birimi</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>Katsayı</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Dönüşüm</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Açıklama</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center', width: 200 }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {donusumler.map(d => (
                  <tr key={d.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600 }}>
                      <div>{d.malzeme_ad}</div>
                      <div style={{ fontSize: 11, color: colors.textTertiary }}>{d.malzeme_kod}</div>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <span style={{ background: '#fff3e0', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: 12 }}>{d.alis_birimi}</span>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', color: colors.textTertiary }}>→</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <span style={{ background: '#e8f5e9', padding: '2px 8px', borderRadius: 4, fontWeight: 600, fontSize: 12 }}>{d.stok_birimi}</span>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, fontSize: 15, color: '#1a73e8' }}>×{d.katsayi}</td>
                    <td style={{ padding: '10px 12px', color: '#555' }}>1 {d.alis_birimi} = {d.katsayi} {d.stok_birimi}</td>
                    <td style={{ padding: '10px 12px', color: '#888', fontSize: 12 }}>{d.aciklama || '-'}</td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <button onClick={() => setRebuildId(d.id)} title="Geçmişi Düzelt" style={{
                        padding: '4px 8px', background: '#fff3e0', color: '#e65100', border: 'none',
                        borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600, marginRight: 4
                      }}>Geçmişi Düzelt</button>
                      <button onClick={() => handleEdit(d)} style={{
                        padding: '4px 10px', background: '#e3f2fd', color: '#1565c0', border: 'none',
                        borderRadius: 4, cursor: 'pointer', fontSize: 12, marginRight: 4
                      }}>✏️</button>
                      <button onClick={() => handleDelete(d.id)} style={{
                        padding: '4px 10px', background: '#ffebee', color: '#c62828', border: 'none',
                        borderRadius: 4, cursor: 'pointer', fontSize: 12
                      }}>🗑️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {rebuildId && (
        <RebuildHistoryModal
          donusumId={rebuildId}
          onClose={() => setRebuildId(null)}
          onSuccess={(result) => {
            const etkilenen = result?.etkilenen ?? 0;
            const recete = result?.etkilenen_recete_sayisi ?? 0;
            const hataSayisi = result?.recompute_hatalari?.length || 0;
            const recetePart = recete ? `, ${recete} reçete güncellendi` : '';
            const hataPart = hataSayisi ? ` (uyarı: ${hataSayisi} reçete recompute hatası)` : '';
            setMsg({
              type: hataSayisi ? 'error' : 'success',
              text: `${etkilenen} kayıt dönüştürüldü${recetePart}${hataPart}.`,
            });
            loadData();
          }}
        />
      )}
    </div>
  );
}
